import { ProductPageData } from "../_types";

export const JSON_PRODUCT_MAJIK_BUWIZ: ProductPageData = {
  id: "majik-buwiz",
  version: "0.1.1",
  timestamp: new Date("2026-05-13").toISOString(),
  lastUpdate: new Date("2026-05-13").toISOString(),
  isEnabled: true,

  repository: {
    url: "https://github.com/Majikah/majik-buwiz-desktop-releases",
    owner: "Majikah",
    repo: "majik-buwiz-desktop-releases",
  },

  metadata: {
    productName: "Majik Buwiz",
    productSlug: "majik-buwiz",
    tagline: "Post-Quantum Invoice Management",
    description:
      "A free, offline-first invoice management app for Windows built for freelancers, consultants, and small business owners. Every invoice is cryptographically signed by default using post-quantum algorithms — tamper-proof, privately yours.",
    seo: "Majik Buwiz — free post-quantum invoice management for Windows. Offline-first, dual-signature, end-to-end encrypted invoice exchange. Built for freelancers and small businesses.",
    keywords: [
      "invoice management",
      "post-quantum cryptography",
      "offline invoice app",
      "freelancer invoicing",
      "small business invoicing",
      "digital signature invoice",
      "encrypted invoice",
      "ML-DSA-87",
      "ML-KEM-768",
      "Ed25519",
      "Windows invoice app",
      "Tauri app",
      "VAT invoicing",
      "EWT invoicing",
      "tamper-proof invoice",
      "zero-knowledge relay",
    ],
    category: "Finance & Invoicing",
    subcategory: "Invoice Management",
    photo: {
      thumbnail:
        "https://storage.majikah.solutions/public/Logo_MajikBuwiz_Majikah_Thumbnail_WEBP.webp",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikBuwiz_SocialCard.webp",
      banner:
        "https://storage.majikah.solutions/public/MajikBuwiz_SmallTile.jpg",
    },
    video: [],
  },

  hero: {
    title: "Majik Buwiz",
    subtitle: "Post-Quantum Invoice Management",
    description:
      "Create, sign, and share invoices with next-generation cryptographic protection — fully offline, completely yours. Free forever.",
    ctaButtons: [
      {
        label: "Download on Microsoft Store",
        url: "https://apps.microsoft.com/detail/9mzz1gm9238r",
        variant: "primary",
        external: true,
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-buwiz-desktop-releases",
        variant: "secondary",
        external: true,
      },
      {
        label: "Web App (Coming Soon)",
        url: "https://buwiz.majikah.solutions",
        variant: "tertiary",
        external: true,
      },
      {
        label: "Read Docs",
        url: "/products/majik-buwiz/docs",
        variant: "tertiary",
      },
    ],
  },

  overview: {
    title: "What is Majik Buwiz?",
    paragraphs: [
      "Majik Buwiz (from the Tagalog word buwis, meaning tax) is a professional invoice management application built for freelancers, consultants, and small business owners. It is designed around two core principles: your data stays on your device, and every invoice you issue is cryptographically protected by default.",
      "Most invoicing tools store your data on someone else's servers. Majik Buwiz works the other way around. All invoice management, exports, and financial analytics work entirely offline — no account required, no internet needed. When you're ready to share, the built-in Invoice Cloud Exchange sends invoices end-to-end encrypted to your recipients.",
      "Majik Buwiz is completely free to use. No subscriptions, no hidden fees, no usage limits.",
    ],
    highlights: [
      {
        icon: "shield",
        label: "Post-Quantum Security",
        description:
          "Every invoice is sealed with dual digital signatures — Ed25519 and ML-DSA-87 (FIPS-204) — making tampering immediately detectable.",
      },
      {
        icon: "offline",
        label: "Offline-First",
        description:
          "Full functionality without internet. Your invoices live on your device by default — no forced cloud, no account required.",
      },
      {
        icon: "lock",
        label: "Your Keys, Your Data",
        description:
          "Cryptographic keys are generated and stored locally, protected by Argon2id passphrase hashing. They never leave your device.",
      },
      {
        icon: "free",
        label: "Free Forever",
        description:
          "No subscriptions, no usage caps, no hidden fees. Majik Buwiz is free for personal and commercial use under the Apache 2.0 license.",
      },
    ],
  },

  features: [
    {
      id: "invoice-management",
      title: "Advanced Invoice Management",
      description:
        "A complete invoicing workflow from creation to payment — with full control over every detail.",
      icon: "document",
      content: [
        "Build fully customized invoices with line items, notes, tags, and reference attachments.",
        "Manage invoices through their full lifecycle: Draft, Issued, Sent, Paid, Void, and more.",
        "Perform batch operations across multiple invoices with advanced filtering and pagination.",
        "Manage client and business contacts with full tax profile metadata.",
      ],
      list: [
        {
          label: "Custom Invoice Builder",
          text: "Add line items, taxes, notes, tags, and reference attachments to every invoice.",
          type: "check",
        },
        {
          label: "Invoice Lifecycle Tracking",
          text: "Move invoices through Draft, Issued, Sent, Paid, Void, and custom states.",
          type: "check",
        },
        {
          label: "Batch Operations",
          text: "Apply actions across multiple invoices at once with filtering and pagination.",
          type: "check",
        },
        {
          label: "Contact Management",
          text: "Store recipient and business contact records with full tax profile metadata.",
          type: "check",
        },
      ],
    },
    {
      id: "tax-system",
      title: "Multi-Layer Tax & Withholding System",
      description:
        "Handles complex tax structures common in Philippine freelancing and small business billing — all calculated automatically.",
      icon: "calculator",
      content: [
        "Supports VAT, Expanded Withholding Tax (EWT), percentage-based taxes, and additive and withholding tax combinations.",
        "Totals, balances, and net payable amounts are calculated automatically as you build the invoice.",
        "Tax breakdowns are reflected in both individual invoices and the financial dashboard.",
      ],
      list: [
        { label: "VAT", text: "Value-Added Tax support.", type: "check" },
        {
          label: "EWT",
          text: "Expanded Withholding Tax support.",
          type: "check",
        },
        {
          label: "Percentage-Based Taxes",
          text: "Flexible percentage tax rules.",
          type: "check",
        },
        {
          label: "Additive & Withholding Combinations",
          text: "Layer multiple tax types on a single invoice.",
          type: "check",
        },
        {
          label: "Automatic Calculations",
          text: "Totals, deductions, and net payable computed in real time.",
          type: "check",
        },
      ],
    },
    {
      id: "financial-dashboard",
      title: "Financial Dashboard & KPI Insights",
      description:
        "Turn your invoice data into actionable financial intelligence — no spreadsheets required.",
      icon: "chart",
      content: [
        "Track gross revenue, amount collected, and outstanding balances at a glance.",
        "Monitor overdue invoices and upcoming due dates with automatic alerts.",
        "Understand your tax position with output tax, withholding tax, and effective tax rate breakdowns.",
        "Analyze payment behaviour with average payment time, invoice size distribution, and client-level revenue insights.",
      ],
      list: [
        {
          label: "Revenue Overview",
          text: "Gross, collected, and outstanding balances.",
          type: "check",
        },
        {
          label: "Tax Breakdown",
          text: "Output tax, withholding tax, and effective tax rate.",
          type: "check",
        },
        {
          label: "Overdue & Due-Soon Alerts",
          text: "Automatic flags for at-risk invoices.",
          type: "check",
        },
        {
          label: "Payment Time Tracking",
          text: "Average time from issuance to payment.",
          type: "check",
        },
        {
          label: "Invoice Size Analytics",
          text: "Average, median, largest, and smallest invoice values.",
          type: "check",
        },
        {
          label: "Client Revenue Insights",
          text: "Revenue segmented by recipient.",
          type: "check",
        },
      ],
    },
    {
      id: "secure-exchange",
      title: "End-to-End Encrypted Invoice Exchange",
      description:
        "Send and receive invoices directly with clients and collaborators — fully encrypted, no payment processor involved.",
      icon: "send",
      content: [
        "Send invoices directly to other Majik Buwiz users through the built-in Invoice Cloud Exchange.",
        "All invoices sent through the Exchange are automatically upgraded to encrypted-and-signed mode before transmission.",
        "The Majikah relay server stores only encrypted ciphertext — it cannot read your invoice contents.",
        "Encrypted invoice data is automatically deleted from the relay after 30 days unless updated by participants.",
        "Sending and receiving invoices online requires a Majik Universal ID (MUID). All other features work fully offline without an account.",
      ],
    },
    {
      id: "exports",
      title: "PDF & CSV Export",
      description:
        "Export invoices in professional formats for sharing, printing, and accounting.",
      icon: "export",
      content: [
        "Export any invoice as a professional PDF for client delivery, printing, or email attachment.",
        "Export invoice data as CSV for accounting software imports, bookkeeping, and record-keeping.",
      ],
      list: [
        {
          label: "PDF Export",
          text: "Professional invoice PDFs for sharing and printing.",
          type: "check",
        },
        {
          label: "CSV Export",
          text: "Structured data export for accounting tools and bookkeeping.",
          type: "check",
        },
      ],
    },
  ],

  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Stack",
        description:
          "All cryptographic operations run locally on your device using NIST-standardized algorithms.",
        table: {
          headers: ["Algorithm", "Standard", "Purpose", "Library"],
          rows: [
            {
              cells: [
                "Ed25519",
                "RFC 8032",
                "Classical invoice signing",
                "@stablelib/ed25519",
              ],
            },
            {
              cells: [
                "ML-DSA-87 (CRYSTALS-Dilithium)",
                "NIST FIPS-204",
                "Post-quantum invoice signing",
                "@noble/post-quantum",
              ],
            },
            {
              cells: [
                "ML-KEM-768 (CRYSTALS-Kyber)",
                "NIST FIPS-203",
                "Post-quantum key encapsulation",
                "@noble/post-quantum",
              ],
            },
            {
              cells: [
                "AES-256-GCM",
                "NIST FIPS-197 / SP 800-38D",
                "Symmetric invoice payload encryption",
                "@stablelib/aes + @stablelib/gcm",
              ],
            },
            {
              cells: [
                "SHA-256",
                "FIPS-180-4",
                "Content hash for signing commitment",
                "@stablelib/sha256",
              ],
            },
            {
              cells: [
                "SHA3-512",
                "FIPS-202",
                "Seal hash computation",
                "@noble/hashes",
              ],
            },
            {
              cells: [
                "Argon2id",
                "RFC 9106",
                "Passphrase-based key derivation",
                "@noble/hashes",
              ],
            },
            {
              cells: [
                "BIP-39 (PBKDF2-SHA512)",
                "BIP-39",
                "Seed phrase to 64-byte seed derivation",
                "bip39",
              ],
            },
          ],
        },
      },
      {
        title: "Platform & Runtime",
        table: {
          headers: ["Component", "Technology", "Notes"],
          rows: [
            {
              cells: [
                "Desktop Runtime",
                "Tauri",
                "Lightweight native Windows app — low memory and disk footprint",
              ],
            },
            {
              cells: [
                "Frontend",
                "React + TypeScript",
                "Built with Vite for fast builds and startup",
              ],
            },
            {
              cells: [
                "Platform",
                "Windows",
                "Available on Microsoft Store; web app coming soon",
              ],
            },
            {
              cells: [
                "License",
                "Apache 2.0",
                "Free for personal and commercial use",
              ],
            },
          ],
        },
      },
      {
        title: "Offline vs. Online Capabilities",
        description: "All core features work without an internet connection.",
        table: {
          headers: ["Feature", "Works Offline?", "Requires Account?"],
          rows: [
            {
              cells: ["Create, edit, and manage invoices", "Yes", "No"],
            },
            {
              cells: ["Sign invoices (Ed25519 + ML-DSA-87)", "Yes", "No"],
            },
            {
              cells: [
                "Encrypt invoices (ML-KEM-768 + AES-256-GCM)",
                "Yes",
                "No",
              ],
            },
            {
              cells: ["Dashboard summary and charts", "Yes", "No"],
            },
            {
              cells: ["Export invoices as PDF", "Yes", "No"],
            },
            {
              cells: ["Export invoices as CSV", "Yes", "No"],
            },
            {
              cells: [
                "Send invoices via Invoice Cloud Exchange",
                "No",
                "Yes (MUID required)",
              ],
            },
            {
              cells: [
                "Receive invoices via Invoice Cloud Exchange",
                "No",
                "Yes (MUID required)",
              ],
            },
          ],
        },
      },
    ],
  },

  howItWorks: {
    title: "How It Works",
    processes: [
      {
        title: "Creating and Signing an Invoice",
        description:
          "Every invoice goes through a structured lifecycle from creation to payment.",
        steps: [
          {
            label: "Build",
            text: "Open the invoice builder and add your recipient, line items, taxes, notes, and references. Your business profile is pre-filled as the sender.",
            type: "numbered",
          },
          {
            label: "Save as Draft",
            text: "Save the invoice as a Draft at any time. Drafts are fully editable — change anything before signing.",
            type: "numbered",
          },
          {
            label: "Sign",
            text: "When ready, sign the invoice. Majik Buwiz automatically seals it with both Ed25519 and ML-DSA-87 signatures. Once signed, the financial content is locked — any modification will break the signature.",
            type: "numbered",
          },
          {
            label: "Issue & Share",
            text: "Export as PDF or CSV for manual delivery, or send it directly through the Invoice Cloud Exchange for end-to-end encrypted delivery.",
            type: "numbered",
          },
          {
            label: "Track",
            text: "Move the invoice through its lifecycle — Issued, Sent, Paid — and monitor it from the financial dashboard.",
            type: "numbered",
          },
        ],
        note: "Fields that change during normal workflow — status, notes, tags, and payment records — are intentionally excluded from the signature. This means a signed invoice can move through its lifecycle without invalidating the original signature.",
      },
      {
        title: "Setting Up Your Majik Key",
        description:
          "Your Majik Key is a local, seed-phrase-based cryptographic identity. It is required before you can create your first invoice.",
        steps: [
          {
            label: "Create",
            text: "On first launch, choose Create new key. Set a strong passphrase — this protects your key locally using Argon2id hashing.",
            type: "numbered",
          },
          {
            label: "Back Up",
            text: "Write down your 12/24-word seed phrase and store it somewhere secure. This is the only recovery path if you lose your device or forget your passphrase.",
            type: "numbered",
          },
          {
            label: "Unlock Each Session",
            text: "Each time you open Majik Buwiz, enter your passphrase to unlock your key for that session. The key is cleared from memory when the app closes.",
            type: "numbered",
          },
        ],
        note: "If you already use Majik Message or Majik Signature, you can import the same Majik Key into Majik Buwiz — all Majikah products share the same key management layer.",
      },
    ],
  },

  pricing: {
    title: "Pricing",
    model: "free",
    currentStatus:
      "Majik Buwiz is completely free. No subscriptions, no usage limits, no hidden fees. It is open-source under the Apache 2.0 license, free for both personal and commercial use.",
    futureStatus:
      "Future versions may introduce optional paid features for teams and enterprise workflows. Core invoicing and security features will remain free.",
  },

  security: {
    title: "Security & Privacy",
    categories: [
      {
        title: "Invoice Integrity",
        items: [
          {
            label: "Dual Digital Signatures",
            description:
              "Every invoice is automatically sealed with Ed25519 (classical) and ML-DSA-87 (post-quantum) signatures. If any field is modified after signing, the signature fails verification immediately.",
          },
          {
            label: "Invoice Sealing",
            description:
              "After all expected signers have signed, an issuer can permanently seal an invoice. The seal is computed as a SHA3-512 hash over all signature entries and cannot be reversed.",
          },
          {
            label: "Tamper Detection",
            description:
              "No one can issue a valid Majik Buwiz invoice on your behalf without your private key. Invoice fraud and impersonation are cryptographically prevented.",
          },
        ],
      },
      {
        title: "Key & Identity Protection",
        items: [
          {
            label: "Local Key Storage",
            description:
              "Your private keys are generated and stored on your device only. They are never transmitted to any server by default.",
          },
          {
            label: "Argon2id Passphrase Hashing",
            description:
              "All private key material is encrypted at rest using AES-256-GCM with a key derived from your passphrase via Argon2id — tuned for memory-hardness to resist brute-force attacks.",
          },
          {
            label: "Session-Based Unlock",
            description:
              "Your key is decrypted into memory only for the active session and cleared automatically when the app is closed. It is never written to disk in plaintext.",
          },
        ],
      },
      {
        title: "Encrypted Sharing",
        items: [
          {
            label: "Optional Invoice Encryption",
            description:
              "For confidential invoices, an optional encryption layer uses ML-KEM-768 key encapsulation and AES-256-GCM to make the invoice contents completely opaque to anyone without the recipient's private key.",
          },
          {
            label: "Zero-Knowledge Relay",
            description:
              "The Invoice Cloud Exchange relay stores only encrypted ciphertext. The Majikah server cannot read your invoice contents at any point.",
          },
          {
            label: "Public Summary Only",
            description:
              "Even on encrypted invoices, only a minimal public summary is stored in plaintext — issuer name, recipient name, currency, total, and status. No line-item detail or sensitive financial data is exposed.",
          },
        ],
      },
      {
        title: "Transparency",
        items: [
          {
            label: "NIST-Standardized Algorithms",
            description:
              "Majik Buwiz uses FIPS-203 (ML-KEM-768) and FIPS-204 (ML-DSA-87) — NIST-standardized post-quantum algorithms. No proprietary or experimental cryptography is used.",
          },
          {
            label: "Open Source",
            description:
              "Majik Buwiz is open-source under the Apache 2.0 license. The full codebase is publicly available for review at github.com/Majikah/majik-buwiz-desktop-releases.",
          },
          {
            label: "No Formal Audit",
            description:
              "No third-party security audit has been conducted and no FIPS compliance certification is claimed. Cryptographic primitives are implemented via @noble/post-quantum and @stablelib.",
          },
        ],
      },
    ],
  },

  useCases: [
    {
      title: "Freelancers",
      description:
        "Issue signed, tamper-proof invoices to clients independently. Keep all records on your own device with no reliance on third-party billing platforms.",
    },
    {
      title: "Consultants",
      description:
        "Handle sensitive client engagements with cryptographically verified invoices. Prove authenticity and protect against invoice disputes with immutable digital signatures.",
    },
    {
      title: "Solo Entrepreneurs",
      description:
        "Get a clear financial picture without cloud dependency. Track revenue, taxes, and outstanding balances from a local-first dashboard built around your workflow.",
    },
    {
      title: "Service Providers in Privacy-Sensitive Industries",
      description:
        "Issue invoices that are optionally fully encrypted — so only your client can read the contents. Nothing sensitive is exposed in transit or in storage.",
    },
    {
      title: "Small Business Owners",
      description:
        "Manage a growing client base, apply complex tax rules (VAT, EWT, withholding), and exchange invoices securely — all from a single lightweight Windows application.",
    },
  ],

  gettingStarted: {
    title: "Getting Started",
    steps: [
      {
        title: "Install Majik Buwiz",
        items: [
          "Search for Majik Buwiz in the Microsoft Store, or visit apps.microsoft.com/detail/9mzz1gm9238r directly.",
          "Click Get and follow the installation prompts.",
          "Launch the app — the onboarding flow starts automatically on first run.",
        ],
      },
      {
        title: "Create Your Majik Key",
        items: [
          "Choose Create new key and set a strong passphrase. This passphrase protects your cryptographic identity locally.",
          "Write down your 12/24-word seed phrase and store it securely — this is the only recovery path if you lose access.",
          "If you already have a Majik Key from Majik Message or Majik Signature, choose Import existing key using your .json seed file, .png backup image, or seed phrase.",
        ],
      },
      {
        title: "Set Up Your Business Profile",
        items: [
          "Fill in your legal name or business name, TIN, address, and other billing details.",
          "This information is stored locally and used as the sender on all invoices you issue.",
        ],
      },
      {
        title: "Create Your First Invoice",
        items: [
          "Go to the My Invoices tab and click New Invoice.",
          "Add your recipient, line items, taxes, notes, and references.",
          "Save as Draft, then sign when ready — your key seals it with dual signatures automatically.",
          "Export as PDF or CSV, or send it via the Invoice Cloud Exchange.",
        ],
      },
      {
        title: "Optional — Set Up a Majik Universal ID",
        items: [
          "A Majik Universal ID (MUID) is only required if you want to send or receive invoices through the Invoice Cloud Exchange.",
          "You can skip this during onboarding and set it up later from the Majik Universal ID tab.",
        ],
      },
    ],
  },

  roadmap: {
    title: "What's Coming Next",
    description:
      "Majik Buwiz is actively developed. Planned enhancements include expanded business features, deeper financial intelligence, and team support.",
    items: [
      {
        label: "Full Audit Logs",
        description:
          "Complete traceable history of all changes, signatures, and status transitions per invoice.",
        status: "planned",
      },
      {
        label: "Team & Organization Support",
        description:
          "Multi-user workflows with shared invoice management and role-based access.",
        status: "planned",
      },
      {
        label: "Advanced Financial Insights",
        description:
          "Expanded analytics, forecasting, and automation on top of the existing financial dashboard.",
        status: "planned",
      },
      {
        label: "Medium-to-Enterprise Features",
        description:
          "Scalable features for growing businesses — approval flows, multi-entity support, and more.",
        status: "planned",
      },
      {
        label: "Web App",
        description:
          "Access Majik Buwiz from any modern browser at buwiz.majikah.solutions.",
        status: "coming-soon",
      },
    ],
  },

  ecosystem: {
    title: "The Majikah Ecosystem",
    paragraphs: [
      "Majik Buwiz is built on top of a suite of open-source Majikah cryptographic libraries — each independently usable and published on npm.",
      "@majikah/majik-invoice provides the core invoice domain model with an optional cryptographic envelope for signing and encryption. @majikah/majik-key is the seed-phrase account library that manages all key derivation and storage. @majikah/majik-signature is the hybrid post-quantum signing library combining Ed25519 and ML-DSA-87 for tamper-proof signatures over any content format. @majikah/majik-envelope is the post-quantum encryption engine handling multi-recipient key encapsulation and authenticated encryption.",
      "All Majikah products — including Majik Message and Majik Signature — share the same key management layer. A single Majik Key works across the entire ecosystem.",
    ],
  },

  cta: {
    title: "Start Invoicing Securely",
    description:
      "Free to download. No account required to get started. Your invoices, your keys, your device.",
    downloads: [
      {
        platform: "Microsoft Store (Windows)",
        url: "https://apps.microsoft.com/detail/9mzz1gm9238r",
      },
      {
        platform: "GitHub Releases",
        url: "https://github.com/Majikah/majik-buwiz-desktop-releases/releases",
      },
    ],
    closingStatement:
      "Post-Quantum Invoice Management for modern businesses — secure, offline-first, and just like magic.",
  },
};
